// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the Situation room's own layers in this browser (slice CR5): which
 * of the room's layers exist, which this browser shows (a per-browser
 * choice, remembered here; a wall shows them all), their colours, and their
 * GeoJSON once fetched. Every globe on the page draws the shown ones
 * (`ownLayersOnMap.drawOwnLayers`), including globes added later.
 */

import type { Map as MaplibreMap } from 'maplibre-gl'
import { drawOwnLayers, type OwnLayerDraw } from './ownLayersOnMap'
import { fetchLayer, type LayerAccess } from './situationClient'
import type { SituationLayerInfo } from './situationRoom'

/** One colour per layer, by its place in the list: bright, apart from each other and from the data palettes' blues. */
export const OWN_LAYER_COLOURS = ['#ffb300', '#00e5ff', '#ff4081', '#76ff03', '#e040fb', '#ff6e40', '#ffd740', '#69f0ae', '#b388ff', '#ffffff'] as const

const HIDDEN_KEY = 'ayni-own-layers-hidden'

export interface OwnLayersController {
  /** The room's layers, in the room's order. */
  list(): readonly SituationLayerInfo[]
  /** The room's layers changed (a reload, an upload, a removal): fetch what is shown and redraw. */
  setList(infos: readonly SituationLayerInfo[], access: LayerAccess): void
  shown(id: string): boolean
  setShown(id: string, on: boolean): void
  colour(id: string): string
  /** Whether a shown layer could not be fetched. */
  failed(id: string): boolean
  /** Draw on these globes now, and again whenever something changes. */
  attach(maps: () => ReadonlyArray<MaplibreMap | null>): void
  /** Draw again (a globe was added). */
  redraw(): void
  onChange(listener: () => void): () => void
}

function storage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

export function createOwnLayers(opts: { remember?: boolean; fetchImpl?: typeof fetch } = {}): OwnLayersController {
  let infos: SituationLayerInfo[] = []
  let access: LayerAccess = { kind: 'member' }
  const data = new Map<string, GeoJSON.FeatureCollection>()
  const failures = new Set<string>()
  const pending = new Set<string>()
  const listeners = new Set<() => void>()
  let mapsOf: () => ReadonlyArray<MaplibreMap | null> = () => []
  const store = opts.remember === false ? null : storage()
  const hidden = new Set<string>(((): string[] => {
    try {
      const raw = JSON.parse(store?.getItem(HIDDEN_KEY) ?? '[]')
      return Array.isArray(raw) ? raw.filter((x): x is string => typeof x === 'string') : []
    } catch {
      return []
    }
  })())

  const changed = (): void => {
    draw()
    for (const l of listeners) l()
  }

  const colour = (id: string): string => {
    const i = infos.findIndex((l) => l.id === id)
    return OWN_LAYER_COLOURS[(i < 0 ? 0 : i) % OWN_LAYER_COLOURS.length]
  }

  function draws(): OwnLayerDraw[] {
    return infos.flatMap((l) => {
      const fc = data.get(l.id)
      return !hidden.has(l.id) && fc ? [{ id: l.id, data: fc, color: colour(l.id) }] : []
    })
  }

  function draw(): void {
    const layers = draws()
    for (const map of mapsOf()) {
      if (!map) continue
      // 'idle' comes again and again (unlike 'load'), so a globe still building its style gets them later.
      if (!drawOwnLayers(map, layers)) map.once('idle', () => drawOwnLayers(map, draws()))
    }
  }

  function fetchShown(): void {
    for (const l of infos) {
      if (hidden.has(l.id) || data.has(l.id) || pending.has(l.id)) continue
      pending.add(l.id)
      const asked = access
      void fetchLayer(l.id, asked, opts.fetchImpl).then((fc) => {
        pending.delete(l.id)
        if (asked !== access || !infos.some((x) => x.id === l.id)) return
        if (fc) {
          data.set(l.id, fc)
          failures.delete(l.id)
        } else failures.add(l.id)
        changed()
      })
    }
  }

  return {
    list: () => infos,
    setList(next, how) {
      if (how.kind !== access.kind || JSON.stringify(how) !== JSON.stringify(access)) {
        data.clear()
        failures.clear()
      }
      access = how
      const ids = new Set(next.map((l) => l.id))
      for (const id of [...data.keys()]) if (!ids.has(id)) data.delete(id)
      infos = [...next]
      fetchShown()
      changed()
    },
    shown: (id) => !hidden.has(id),
    setShown(id, on) {
      if (on) hidden.delete(id)
      else hidden.add(id)
      try {
        store?.setItem(HIDDEN_KEY, JSON.stringify([...hidden]))
      } catch {
        /* remembered for this page only */
      }
      fetchShown()
      changed()
    },
    colour,
    failed: (id) => failures.has(id),
    attach(maps) {
      mapsOf = maps
      draw()
    },
    redraw: draw,
    onChange(listener) {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
  }
}
